import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import Grow from '@mui/material/Grow';
import React, { useCallback, useState } from 'react';
import useAnchorMenu from '../../../../../hooks/useAnchorMenu';
import useTimeout from '../../../../../hooks/useTimeout';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { addDivider, addList } from '../../../../../redux/trip/trip.slice';
import { selectCurrentUser } from '../../../../../redux/user/user.selectors';
import DropdownMenu from '../../../../shared/DropdownMenu';
import './AddListInput.scss';

type AddListInputProps = {
  tripId: string | undefined;
  canEdit?: boolean;
  listOrder: string[] | undefined;
};

const DEFAULT_LIST_EMOJI = '🏝️';
const DEFAULT_SECTION_EMOJI = '🗂️';
const MAX_CHARACTER_COUNT = 100;
const CANCEL_TIMEOUT = 100;

function AddListInput({ tripId, canEdit, listOrder }: AddListInputProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  const menu = useAnchorMenu<HTMLDivElement>();

  const [newListName, setNewListName] = useState('');
  const [isAddingList, setIsAddingList] = useState(false);
  const cancelListTimeout = useTimeout();

  const [newSectionName, setNewSectionName] = useState('');
  const [isAddingSection, setIsAddingSection] = useState(false);
  const cancelSectionTimeout = useTimeout();

  // --- Menu ---

  const handleMenuOpen = menu.open;

  // --- List ---

  const onAddList = useCallback(() => {
    setIsAddingList(false);
    if (tripId && currentUser && newListName.trim().length > 0) {
      dispatch(
        addList({
          tripId,
          list: { name: newListName.trim(), emoji: DEFAULT_LIST_EMOJI, creator: currentUser.uid },
          listOrder: listOrder || [],
        })
      );
      setNewListName('');
    }
  }, [tripId, currentUser, newListName, listOrder, dispatch]);

  const cancelAddList = useCallback(() => {
    cancelListTimeout.set(() => {
      setIsAddingList(false);
      setNewListName('');
    }, CANCEL_TIMEOUT);
  }, [cancelListTimeout]);

  const handleListKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const text = (event.target as HTMLInputElement).value;

      if (event.key === 'Enter' && text.trim().length > 0) {
        onAddList();
        return;
      }

      if (event.key === 'Escape') {
        cancelAddList();
        return;
      }

      if (
        text.length >= MAX_CHARACTER_COUNT &&
        event.key !== 'Backspace' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight'
      ) {
        event.preventDefault();
      }
    },
    [onAddList, cancelAddList]
  );

  // --- Section ---

  const onAddSection = useCallback(() => {
    setIsAddingSection(false);
    if (tripId && newSectionName.trim().length > 0) {
      dispatch(
        addDivider({
          tripId,
          divider: { name: newSectionName.trim(), emoji: DEFAULT_SECTION_EMOJI, listOrder: [] },
          listOrder: listOrder || [],
        })
      );
      setNewSectionName('');
    }
  }, [tripId, newSectionName, listOrder, dispatch]);

  const cancelAddSection = useCallback(() => {
    cancelSectionTimeout.set(() => {
      setIsAddingSection(false);
      setNewSectionName('');
    }, CANCEL_TIMEOUT);
  }, [cancelSectionTimeout]);

  const handleSectionKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const text = (event.target as HTMLInputElement).value;

      if (event.key === 'Enter' && text.trim().length > 0) {
        onAddSection();
        return;
      }

      if (event.key === 'Escape') {
        cancelAddSection();
        return;
      }

      if (
        text.length >= MAX_CHARACTER_COUNT &&
        event.key !== 'Backspace' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight'
      ) {
        event.preventDefault();
      }
    },
    [onAddSection, cancelAddSection]
  );

  const isAdding = isAddingList || isAddingSection;

  return (
    <>
      <div className='lists-label'>
        LISTS
        {canEdit && !isAdding && (
          <div
            className='add-list-div'
            onClick={handleMenuOpen}
          >
            <svg
              className='plus-svg'
              viewBox='0 0 455 455'
            >
              <polygon points='455,212.5 242.5,212.5 242.5,0 212.5,0 212.5,212.5 0,212.5 0,242.5 212.5,242.5 212.5,455 242.5,455 242.5,242.5 455,242.5' />
            </svg>
          </div>
        )}
      </div>

      <DropdownMenu
        {...menu.menuProps}
        align='start'
      >
        <DropdownMenu.Item
          deferUntilClosed
          onSelect={() => setIsAddingList(true)}
        >
          New List
        </DropdownMenu.Item>
        <DropdownMenu.Item
          deferUntilClosed
          onSelect={() => setIsAddingSection(true)}
        >
          New Divider
        </DropdownMenu.Item>
      </DropdownMenu>

      <Grow
        in={isAddingList}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 1000, exit: 400 }}
      >
        <div>
          <input
            className='styled-input'
            placeholder='e.g. Restaurants'
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onBlur={cancelAddList}
            onKeyDown={handleListKeyDown}
            autoFocus
          />
          <div className='new-list-buttons'>
            <div
              className='cancel-add-item-div'
              onClick={cancelAddList}
            >
              <CloseRoundedIcon className='cancel-add-item-button' />
              <span className='cancel-span'>CANCEL</span>
            </div>
            {newListName.trim().length > 0 && (
              <div
                className='add-item-div'
                onClick={onAddList}
              >
                <CheckRoundedIcon className='save-add-item-button' />
                <span className='add-span'>SAVE</span>
              </div>
            )}
          </div>
        </div>
      </Grow>

      <Grow
        in={isAddingSection}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 1000, exit: 400 }}
      >
        <div>
          <input
            className='styled-input'
            placeholder='e.g. Day Trips'
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            onBlur={cancelAddSection}
            onKeyDown={handleSectionKeyDown}
            autoFocus
          />
          <div className='new-list-buttons'>
            <div
              className='cancel-add-item-div'
              onClick={cancelAddSection}
            >
              <CloseRoundedIcon className='cancel-add-item-button' />
              <span className='cancel-span'>CANCEL</span>
            </div>
            {newSectionName.trim().length > 0 && (
              <div
                className='add-item-div'
                onClick={onAddSection}
              >
                <CheckRoundedIcon className='save-add-item-button' />
                <span className='add-span'>SAVE</span>
              </div>
            )}
          </div>
        </div>
      </Grow>
    </>
  );
}

AddListInput.displayName = 'AddListInput';
export default AddListInput;
